import {
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { catchError, Observable, throwError } from 'rxjs';
import { BusinessAuthService } from '../services/business-auth.service';
import { environment } from '../../environments/environment';

/** Formato t?pico de error del backend Nest/API. */
interface BackendErrorBody {
  timestamp?: string;
  path?: string;
  statusCode?: number;
  message?: string;
  error?: string;
}

function apiBase(): string {
  return environment.apiUrl.replace(/\/$/, '');
}

function isBusinessApiRequest(url: string): boolean {
  const base = apiBase();
  if (url.startsWith(base)) {
    return url.includes('/business/');
  }
  return /\/api\/v1\/business\b/.test(url) || (url.startsWith('/') && url.includes('/business/'));
}

function isBusinessPanelApiRequest(url: string): boolean {
  if (url.includes('/auth/business')) {
    return false;
  }
  if (url.includes('/business/master/companies')) {
    return false;
  }
  const base = apiBase();
  const onApi =
    url.startsWith(base) ||
    /\/api\/v1\//.test(url) ||
    (url.startsWith('/') && !url.startsWith('//'));
  if (!onApi) {
    return false;
  }
  return (
    url.includes('/business/') ||
    url.includes('/trip/payment') ||
    url.includes('/trip/business-trip-link') ||
    url.includes('/coupon/business-account/') ||
    url.includes('/geofences/') ||
    url.includes('/pickup-locations/')
  );
}

function readBackendMessage(body: BackendErrorBody | undefined): string {
  const msg = body?.message;
  if (Array.isArray(msg)) {
    return String(msg[0] ?? '');
  }
  if (typeof msg === 'string') {
    return msg;
  }
  return typeof body?.error === 'string' ? body.error : '';
}

/** Errores documentados para sesión maestra sin header o empresa no asignada. */
function resolveMasterCompanyPanelError(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  auth: BusinessAuthService,
): { title: string; message: string; redirectToChooseCompany: boolean } | null {
  if (!auth.isMasterSession() || !isBusinessPanelApiRequest(req.url)) {
    return null;
  }

  const backendMsg = readBackendMessage(error.error as BackendErrorBody | undefined);
  const msgLower = backendMsg.toLowerCase();

  if (error.status === 400) {
    const missingHeader =
      auth.needsMasterCompanySelection() ||
      msgLower.includes('x-business-account-id') ||
      msgLower.includes('business-account-id') ||
      msgLower.includes('business account id') ||
      msgLower.includes('empresa activa') ||
      msgLower.includes('selecciona una empresa');

    if (missingHeader) {
      return {
        title: 'Empresa activa requerida',
        message:
          'Debes elegir la empresa con la que operar en el panel. Se abrirá el selector de empresas.',
        redirectToChooseCompany: true,
      };
    }
  }

  if (error.status === 403) {
    const companyForbidden =
      msgLower.includes('asignada') ||
      msgLower.includes('cuenta maestra') ||
      msgLower.includes('no está asignada');

    if (companyForbidden || auth.isMasterCompanyAccessHttpError(error)) {
      return {
        title: 'Sin acceso a esta empresa',
        message:
          backendMsg ||
          'Esta empresa no está asignada a tu cuenta maestra. Elige otra empresa de la lista.',
        redirectToChooseCompany: true,
      };
    }
  }

  return null;
}

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const alertController = inject(AlertController);
  const router = inject(Router);
  const businessAuth = inject(BusinessAuthService);
  const ngZone = inject(NgZone);

  const excludedUrls: (string | RegExp)[] = [
    'auth/refresh-token',
    'auth/me',
    'driver/me',
    /\/trip\/.*\/location/,
    /\/trip\/trip\/[^/]+\/route/,
    'system-configuration/current',
    'trip-operation/accept',
    '/trip/driver/location',
    /\/trip-operation\/[^/]+\/in-progress/,
    /\/packages\/[^/]+\/pickup/,
    /\/packages\/[^/]+\/deliver/,
    'business/account/password',
    'business-login',
    'business-master-login',
    'auth/business-master-login',
    'business/master/companies',
    'auth/business/validate-reset-token',
    /** Ruta p?blica de seguimiento (sin sesi?n): no mostrar alert global en 4xx/5xx. */
    /\/trip\/public\/nano\/[^/]+\/route/,
    /\/public\/trip\/business-trip-link\//,
    /\/surveys\/by-trip\//,
    'fare-rates/calculate-all-services-google'
  ];

  const isExcludedUrl = (url: string): boolean =>
    excludedUrls.some((excludedUrl) => {
      if (typeof excludedUrl === 'string') {
        return url.includes(excludedUrl);
      }
      return excludedUrl.test(url);
    });

  const showAlert = (title: string, message: string, onConfirm?: () => void): void => {
    void ngZone.run(async () => {
      const alert = await alertController.create({
        header: title,
        message,
        buttons: [
          {
            text: 'Aceptar',
            role: 'confirm',
            handler: () => {
              if (message === 'No existe un viaje registrado con este id') {
                router.navigate(['/'], { replaceUrl: true });
                return;
              }
              onConfirm?.();
            },
          },
        ],
      });
      await alert.present();
    });
  };

  const handleBusinessAuthError = (): void => {
    businessAuth.logout();
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorTitle = 'Aviso';
      let errorMessage = 'Ocurri? un error inesperado';
      let shouldRedirectToLogin = false;

      const body = error.error as BackendErrorBody | undefined;

      console.log('Error interceptor:', {
        status: error.status,
        url: req.url,
        error: error.error,
        message: body?.message,
        errorType: body?.error,
        timestamp: body?.timestamp,
        path: body?.path,
      });

      const masterPanelError = resolveMasterCompanyPanelError(error, req, businessAuth);
      if (masterPanelError && !isExcludedUrl(req.url)) {
        if (masterPanelError.redirectToChooseCompany) {
          businessAuth.redirectToMasterCompanyChooser({
            title: masterPanelError.title,
            message: masterPanelError.message,
          });
          return throwError(() => error);
        }
        showAlert(masterPanelError.title, masterPanelError.message);
        return throwError(() => error);
      }

      switch (error.status) {
        case 400:
          if (error.error) {
            errorTitle = 'Aviso';
            const msg = body?.message;
            if (Array.isArray(msg)) {
              errorMessage = String(msg[0]);
            } else {
              errorMessage =
                (typeof msg === 'string' ? msg : undefined) ||
                body?.error ||
                'Solicitud incorrecta';
            }
          } else {
            errorMessage = 'Solicitud incorrecta. Verifica los datos enviados.';
          }
          break;

        case 401:
          shouldRedirectToLogin = true;
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? '';
          } else {
            errorMessage =
              'Tu sesi?n ha expirado. Por favor, inicia sesi?n nuevamente.';
          }
          break;

        case 403:
          if (body?.message === 'Cuenta eliminada') {
            errorMessage = body.error ?? errorMessage;
          } else {
            errorMessage = 'No tienes permisos para realizar esta acci?n.';
          }

          if (req.url.includes('/trip/by-id/')) {
            return throwError(() => error);
          }
          if (
            req.url.includes('auth/me') ||
            req.url.includes('driver/me') ||
            req.url.includes('driver-earnings/stats/daily') ||
            req.url.includes('driver-earnings/trip-bonus-progress') ||
            req.url.includes('trip/driver/pending') ||
            req.url.includes('packages/driver/pending') ||
            req.url.includes('trip/driver/scheduled')
          ) {
            if (isBusinessApiRequest(req.url)) {
              businessAuth.logout();
            }
            return throwError(() => error);
          }
          break;

        case 404:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? 'El recurso solicitado no fue encontrado.';
          } else {
            errorMessage = 'El recurso solicitado no fue encontrado.';
          }

          if (body?.message === 'Usuario no encontrado') {
            if (isBusinessApiRequest(req.url)) {
              businessAuth.logout();
            }
          }
          break;

        case 409:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? 'Conflicto con el recurso solicitado.';
          } else {
            errorMessage = 'Conflicto con el recurso solicitado.';
          }
          break;

        case 422:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? 'Datos de validaci?n incorrectos.';
          } else {
            errorMessage = 'Datos de validaci?n incorrectos.';
          }
          break;

        case 429:
          errorMessage =
            'Demasiadas solicitudes. Intenta nuevamente en unos minutos.';
          break;

        case 500:
          errorMessage =
            'Error interno del servidor. Intenta nuevamente m?s tarde.';
          break;

        case 502:
        case 503:
        case 504:
          errorMessage =
            'Servicio temporalmente no disponible. Intenta nuevamente m?s tarde.';
          break;

        default:
          if (body?.message) {
            errorTitle = body.message;
            errorMessage = body.error ?? errorMessage;
          } else if (error.message) {
            errorTitle = error.message;
          } else {
            errorMessage =
              'Error de conexi?n. Verifica tu conexi?n a internet.';
          }
          break;
      }

      console.log('errorTitle', errorTitle);
      console.log('errorMessage', errorMessage);

      if (error.status === 0 || error.message?.includes('ERR_CONNECTION_RESET')) {
        return throwError(() => error);
      }

      if (isExcludedUrl(req.url)) {
        return throwError(() => error);
      }

      showAlert(errorTitle, errorMessage);

      if (shouldRedirectToLogin && isBusinessApiRequest(req.url)) {
        handleBusinessAuthError();
      }

      return throwError(() => error);
    })
  );
};
